import { commonShortFieldLabels, commonShortNouns } from "./common-short-fields";

export const examAdminExamsDetail = {
  pageTitle: "Exam Detail",
  notFound: "Exam not found.",
  subtitleCode: "Exam code {code}",
  sections: {
    basicInfo: "Basic Info",
    config: "Exam Config",
    candidates: "Candidate List",
    attempts: "Attempts",
    environments: "Environment Instances",
    submissions: "Code Submissions",
    scores: "Score Records",
  },
  drawers: {
    editTitle: "Edit Exam",
    assignTitle: "Assign Candidates",
    customFieldsTitle: "Exam Custom Fields",
  },
  candidateAssignHint:
    "This only adds assignments and does not overwrite existing assignments. Remove assigned candidates individually from the candidate list.",
  candidateRequired: "Select at least one candidate",
  candidateLabel: "Candidate",
  actions: {
    publishScores: "Publish Scores",
    publish: "Publish",
    startNow: "Start Now",
    start: "Start",
    end: "End",
    archive: "Archive",
    exportRuntime: "Export Results",
    exportScores: "Export Score Details",
    assignCandidates: "Assign Candidates",
    viewAttemptDetail: "View",
    removeCandidate: "Remove",
  },
  messages: {
    candidatesUpdated: "Candidate assignments updated",
    examUpdated: "Exam updated",
    examDeleted: "Exam deleted",
    candidateRemoved: "Candidate removed from exam",
    scorePublished: "Scores published",
    statusUpdated: "Exam status updated to {action}",
    archivedReadonly: "Archived exams are read-only and cannot be edited",
    publishScoresUnavailable: "Manual score publishing is unavailable: {reason}",
    publishScoresAutoMode: "Scores are published automatically; manual publishing is not needed.",
    publishScoresExamNotEnded: "Scores can be published manually only after the exam ends.",
    publishScoresAlreadyPublished: "Scores have already been published.",
    customFieldsSaved: "Exam custom fields saved",
    saveFailed: "Save failed",
  },
  labels: {
    assignedStatus: "Assigned",
    anomalyNormal: "Normal",
    currentPaperVersion: "Current Version",
    historyPaperVersion: "Historical Version",
    scoreSourceAuto: "Auto Scoring",
    scoreSourceEnvironment: "Environment Scoring",
    scoreSourceJudge: "Judge Scoring",
    scoreSourceAi: "AI Scoring",
    scoreSourceManual: "Manual Scoring",
    scoreSourceReview: "Review Scoring",
    unknownSource: "Unknown Source",
    commentAutoObjectiveScoring: "System automatic objective scoring",
  },
  actionsExtra: {
    viewPaper: "View Paper",
    checkPaperUpdates: "Check Updates",
    customFields: "Custom Fields",
  },
  tabs: {
    rules: "Rules",
    candidates: "Candidate Attempts",
    analysis: "Exam Analysis",
    monitoring: "Runtime Monitoring",
    scores: "Score Records",
  },
  ruleGroups: {
    entry: "Exam Entry",
    submission: "Submission and Scoring",
    runtime: "Runtime Policy",
  },
  table: {
    candidate: "Candidate",
    assignmentStatus: "Assignment Status",
    assignedAt: "Assigned At",
    attempt: "Attempt",
    attemptId: "Attempt ID",
    candidateId: "Candidate ID",
    score: "Score",
    anomaly: "Anomaly",
    time: "Time",
    instanceId: "Instance ID",
    submissionId: "Submission ID",
    judgeResult: "Judge Result",
    submittedAt: "Submitted At",
    scoreRecordId: "Record ID",
    source: "Source",
    comment: "Comment",
    includedInFinal: "Included in Final Score",
  },
  candidateTable: {
    startedAt: "Started At",
    submittedAt: "Submitted At",
    usedMinutes: "Time Used",
    usedMinutesValue: "{minutes} min",
    anomaly: "Anomaly",
    objectiveSubjectiveScore: "Objective {objective} / Subjective {subjective}",
  },
  basicInfo: {
    status: commonShortNouns.status,
    paperTitle: "Paper Name",
    paperCode: "Paper Code",
    paperVersionCode: "Paper Version Code",
    paperVersionId: "Paper Version ID",
    entryCode: "Entry Code",
    entrySubdomain: "Entry Subdomain",
    duration: "Exam Duration",
    actualEndTime: "Actual End Time",
    publishScoreMode: "Score Publishing Mode",
    resultVisibility: "Score Visibility",
    answerVisibility: "Answer Visibility",
    allowLateEntry: "Allow Late Entry",
    lateEntryGraceMinutes: "Max Late Entry Grace",
    allowReentry: "Allow Re-entry",
    allowMultipleSubmit: "Allow Multiple Submissions",
    maxAttemptCount: "Max Attempts",
    candidateEnvironmentMaxActive: "Max concurrent containers per candidate",
    candidateEnvironmentMaxTotal: "Max container creations per candidate",
    createdAt: commonShortFieldLabels.createdAt,
  },
  summary: {
    candidates: "Assigned candidates: {count}",
    attempts: "Attempts: {count}",
    environments: "Environment instances: {count}",
    submissions: "Code submissions: {count}",
    anomalies: "Anomalous attempts: {count}",
    scorePublished: "Scores published: {value}",
    scorePublishedAt: "Published at: {value}",
  },
  filters: {
    showAnomaliesOnly: "Show anomalies only",
    attemptStatus: "Filter Status",
    candidate: "Filter Candidate",
    environmentStatus: "Filter Environment Status",
    verdict: "Filter Judge Result",
    scoreSource: "Filter Score Source",
    candidateByCandidate: "Filter by Candidate",
  },
  overviewStats: {
    expectedCandidates: "Expected Candidates",
    participants: "Participants",
    entered: "Entered",
    submitted: "Submitted",
    anomalies: "Anomalies",
  },
  timeFields: {
    hourMinuteSecond: "{hour}h {minute}m {second}s",
    minuteSecond: "{minute}m {second}s",
    second: "{second}s",
  },
  candidateMetrics: {
    attendanceRate: "Attendance Rate",
    attendanceRateHint:
      "Attendance rate = entered / baseline candidates. Assigned exam baseline = expected candidates; open exam baseline = current participants.",
    completionRate: "Completion Rate",
    completionRateHint:
      "Completion rate = submitted / baseline candidates. Assigned exam baseline = expected candidates; open exam baseline = entered candidates.",
    anomalyRate: "Anomaly Rate",
    anomalyRateHint: "Anomaly rate = anomalous candidates / baseline candidates.",
    averageScore: "Average Score",
  },
  candidateFilters: {
    status: "Filter Status",
    keyword: "Search by name/username",
    onlyAnomalies: "Only Anomalies",
    onlySubmitted: "Only Submitted",
    clear: "Clear Filters",
    quickAnomalies: "Show Anomalies",
    quickSubmitted: "Show Submitted",
    exportCurrent: "Export Current Filter",
  },
  analysis: {
    coreMetrics: "Core Metrics",
    averageScore: "Average Score",
    medianScore: "Median Score",
    highestScore: "Highest Score",
    lowestScore: "Lowest Score",
    scoreDistribution: "Score Distribution",
    scoreTypeStats: "Question Type Score Stats",
    submissionBehavior: "Submission Behavior",
    submissionTotal: "Total Code Submissions",
    involvedAttempts: "Involved Attempts",
    avgSubmissionsPerAttempt: "Avg Submissions / Attempt",
    scoreBucketRange: "Score Range",
    scoreBucketCount: "Candidates",
    scoreBucketRatio: "Ratio",
    scoreType: "Question Type",
    scoreRecordCount: "Score Records",
    avgScore: "Average Score",
  },
  scoreSummary: {
    title: "Score Records",
    totalRecords: "Total Score Records",
    questionTypeCount: "Covered Question Types",
    sourceCount: "Score Sources",
    sourceTag: "{source}: {count} records (avg: {avg})",
  },
  empty: {
    extraConfig: "No extended config",
    candidateRows: "No candidate attempt records match the current filters",
    monitoring: "No runtime records. The exam may not have started or has no related data.",
    scores: "No score records. Scoring or submissions may not be complete yet.",
  },
  extraConfigLabels: {
    seedKey: "Seed Key",
    seedBundle: "Seed Bundle",
    typeRuntimePolicies: "Question Type Runtime Policies",
  },
  questionTypes: {
    singleChoice: "Single Choice",
    multipleChoice: "Multiple Choice",
    judgment: "True/False",
    shortAnswer: "Short Answer",
    code: "Coding",
    practical: "Practical",
    attackDefense: "Attack & Defense",
  },
  runtimePolicy: {
    shuffleQuestions: "Question shuffle {value}",
    shuffleOptions: "Option shuffle {value}",
    allowSelfCheck: "Self-check {value}",
    maxSelfCheckCount: "Self-check limit {count}",
  },
  staffRoles: {
    sectionTitle: "Exam Staff Roles",
    searchPlaceholder: "Search staff users",
    clear: "Clear",
    assign: "Assign roles",
    empty: "No exam staff roles assigned",
    assignDrawerTitle: "Assign exam staff roles",
    assignHint: "Assign proctor/reviewer by exam; archived exams are read-only.",
    staffLabel: "Staff users",
    staffRequired: "Please select staff users",
    roleLabel: "Exam roles",
    roleRequired: "Please select at least one role",
    tableStaff: "Staff",
    tableUserStatus: "Account status",
    tableRoles: "Exam roles",
    removeConfirmTitle: "Remove this exam role?",
    removePrefix: "Remove",
  },
  confirm: {
    removeCandidateTitle: "Remove this candidate?",
    removeCandidateDescription:
      "If the candidate already has attempt records, the backend will reject the removal.",
    publishExamTitle: "Publish this exam?",
    publishExamDescription:
      "After publishing, the exam status will become published. Please confirm.",
    startExamTitle: "Start this exam?",
    startExamDescription: "After starting, the exam will enter the running state. Please confirm.",
    start_nowExamTitle: "Start this exam immediately?",
    start_nowExamDescription:
      "The exam will start immediately and candidates can enter right away. Please confirm.",
    publish_scoresExamTitle: "Publish scores?",
    publish_scoresExamDescription:
      "After publishing, candidates will be able to view their exam scores. Please confirm.",
    endExamTitle: "End this exam?",
    endExamDescription:
      "After the exam ends, candidates will no longer be able to answer questions. Please confirm.",
    archiveExamTitle: "Archive this exam?",
    archiveExamDescription: "After archiving, related data will be locked. Please confirm.",
    deleteExamTitle: "Delete this exam?",
    deleteExamDescription: "Only draft exams can be deleted. This action cannot be undone.",
  },
};
